import React, { useState } from 'react';
import { Search } from 'lucide-react';

/**
 * CSearchbar - shared, reusable labeled search field.
 *
 * Renders a single CFiltersBar grid cell: a "Search" label above a relative
 * search input (with a leading magnifier icon and an optional trailing loading
 * spinner). Drops directly into the CFiltersBar responsive grid as one cell.
 *
 * The input carries `text-sm` so its height matches the custom Dropdown trigger.
 * While `loading`, it also gains `pr-10` so long search text cannot run
 * underneath the trailing spinner.
 *
 * Passing `suggestions` turns the field into an autocomplete combobox (WAI-ARIA
 * combobox with a listbox popup): the list shows while the field has focus and
 * text, ArrowUp / ArrowDown move through it, Enter picks, Escape closes. The
 * caller filters the suggestions; this only presents them.
 *
 * @param {object}   props
 * @param {string}   [props.value]                 Controlled input value.
 * @param {Function} [props.onChange]              Input onChange handler.
 * @param {string}   [props.placeholder='Search...'] Placeholder text.
 * @param {boolean}  [props.loading=false]         Show the loading spinner.
 * @param {string[]} [props.suggestions]           Autocomplete entries to offer.
 * @param {Function} [props.onSuggestionSelect]    Called with the picked entry.
 * @param {string}   [props['data-testid']]        Forwarded to the input, so the
 *   e2e suite can target the field the way it did before this was extracted.
 */
const CSearchbar = ({
  value = '',
  onChange,
  placeholder = 'Search...',
  loading = false,
  suggestions,
  onSuggestionSelect,
  'data-testid': dataTestId,
  id = 'c-searchbar',
}) => {
  const [open, setOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const autocomplete = Array.isArray(suggestions);
  const listboxId = `${id}-suggestions`;
  const showList = autocomplete && open && value.trim() !== '' && suggestions.length > 0;
  const activeIndex = showList && highlightedIndex < suggestions.length ? highlightedIndex : -1;

  const pick = (entry) => {
    setOpen(false);
    setHighlightedIndex(-1);
    if (onSuggestionSelect) onSuggestionSelect(entry);
  };

  const handleKeyDown = (e) => {
    if (showList) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex(activeIndex >= suggestions.length - 1 ? 0 : activeIndex + 1);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex(activeIndex <= 0 ? suggestions.length - 1 : activeIndex - 1);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        setOpen(false);
        setHighlightedIndex(-1);
        return;
      }
      if (e.key === 'Enter' && activeIndex >= 0) {
        e.preventDefault();
        pick(suggestions[activeIndex]);
        return;
      }
    } else if (autocomplete && e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      return;
    }
    if (e.key === 'Enter') e.preventDefault();
  };

  const comboboxProps = autocomplete
    ? {
      role: 'combobox',
      'aria-autocomplete': 'list',
      'aria-expanded': showList,
      'aria-controls': showList ? listboxId : undefined,
      'aria-activedescendant': activeIndex >= 0 ? `${listboxId}-${activeIndex}` : undefined,
      autoComplete: 'off',
      onFocus: () => setOpen(true),
      onBlur: () => { setOpen(false); setHighlightedIndex(-1); },
    }
    : {};

  return (
    <div>
      <label
        htmlFor={id}
        className="block text-sm font-medium text-gray-700 mb-1"
      >
        Search
      </label>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
        <input
          id={id}
          type="text"
          {...(dataTestId ? { 'data-testid': dataTestId } : {})}
          placeholder={placeholder}
          value={value}
          onChange={(e) => {
            if (autocomplete) {
              setOpen(true);
              setHighlightedIndex(-1);
            }
            if (onChange) onChange(e);
          }}
          onKeyDown={handleKeyDown}
          {...comboboxProps}
          className={`pl-10 ${loading ? 'pr-10' : ''} w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500`}
        />
        {loading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
          </div>
        )}
        {showList && (
          <ul
            id={listboxId}
            role="listbox"
            aria-label="Suggestions"
            className="absolute z-50 mt-1 max-h-60 w-max min-w-full max-w-xs overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg"
          >
            {suggestions.map((entry, index) => (
              <li
                key={entry}
                id={`${listboxId}-${index}`}
                role="option"
                aria-selected={index === activeIndex}
                className={`cursor-pointer select-none truncate px-3 py-2 text-sm text-gray-900 ${
                  index === activeIndex ? 'bg-gray-100' : 'hover:bg-gray-50'
                }`}
                {...(dataTestId ? { 'data-testid': `${dataTestId}-suggestion` } : {})}
                onMouseDown={(event) => {
                  // Before the input's blur closes the list.
                  event.preventDefault();
                  pick(entry);
                }}
                onMouseEnter={() => setHighlightedIndex(index)}
              >
                {entry}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default CSearchbar;
